/**
 * PanelDisclosure — a collapsible, indentable group whose header carries the node's own toggle.
 *
 * The structural piece the node editor is built from: a region's content genuinely IS containment
 * (city -> blocks -> buildings -> floors), and only an indented tree shows that. It maps 1:1 onto
 * the catalog, so the editor generates it rather than authoring it.
 *
 * WHAT COLLAPSES IS THE BODY THIS IS GIVEN, and the editor gives it one node's parameters — NOT the
 * node's children. Children are rendered after it, at their own depth, so the shape of the world
 * stays on screen and only the ninety sliders come and go.
 *
 * THE HEADER INDENTS AND THE BODY DOES NOT. Indenting the body too would make a slider under Stairs
 * 36 px narrower than one under Landforms — every track a different length, every value box on a
 * different x — and the ends of a range only read when they line up. The body gives up its indent
 * to buy that, and loses nothing: the header naming it is the line directly above.
 *
 * A DISABLED NODE GREYS; IT NEVER DISAPPEARS, AND IT STILL OPENS. The user has to be able to see
 * that "Interiors" exists and why it is unavailable — a control that vanishes is a control they hunt
 * for, and one that refuses to open is one whose reason they cannot read.
 *
 * THE SWITCH IS A TICKBOX, AND IT LEADS THE ROW. Seventeen Off/On tracks down a nested list read as
 * seventeen separate decisions and leave no width for the indent; a tickbox is the shape a hierarchy
 * of on/off things already has. It sits before the chevron so the ticks line up as a column you can
 * scan, which is the whole reason the tree is a tree.
 *
 * THE CLIP COMES OFF AFTER THE TRANSITION. `overflow-hidden` is what makes the height animation
 * read, and leaving it on afterwards cuts off anything that opens upward out of the body (a
 * dropdown, a material popover). ExplorePanel's card has the same note and the same fix.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { PanelCheckbox } from './PanelCheckbox';
import { ACCENT_TEXT, MOTION, ROW_LABEL } from './tokens';

/** Mirrors the body's transition, after which the clip has to come off. */
const EXPAND_MS = 220;

interface PanelDisclosureProps {
  label: string;
  /** Indent depth. The editor caps this rather than the component, because the cap is a layout
   *  decision about the sheet's width, not about what a disclosure can do. */
  depth?: number;
  open: boolean;
  /** Omit for a node with nothing to reveal: the chevron goes and the header stops being a button. */
  onOpenChange?: (open: boolean) => void;
  /** The node's own switch, in the header. Omit for a group that is always present. */
  toggle?: { value: boolean; onChange: (v: boolean) => void; disabled?: boolean; title?: string };
  /** A word or two on the right of the header while collapsed (a count, the chosen model). */
  summary?: string;
  /** Greyed and collapsed: the parent is off, so nothing under here is doing anything. */
  inert?: boolean;
  children: ReactNode;
}

export function PanelDisclosure({
  label, depth = 0, open, onOpenChange, toggle, summary, inert, children,
}: PanelDisclosureProps) {
  const [clip, setClip] = useState(!open);
  useEffect(() => {
    if (!open) { setClip(true); return; }
    const t = setTimeout(() => setClip(false), EXPAND_MS);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <div className={`${MOTION} ${inert ? 'opacity-40' : ''}`}>
      <div
        className="flex items-center gap-1.5"
        style={{ paddingLeft: depth > 0 ? depth * 6 : undefined }}
      >
        {/* A fixed slot either way, so the labels of a node with a switch and one without line up. */}
        <div className="w-5 shrink-0">
          {toggle && (
            <PanelCheckbox
              value={toggle.value}
              onChange={toggle.onChange}
              disabled={toggle.disabled}
              title={toggle.title}
              ariaLabel={`${label} on`}
            />
          )}
        </div>
        <button
          type="button"
          onClick={() => onOpenChange?.(!open)}
          aria-expanded={onOpenChange ? open : undefined}
          disabled={!onOpenChange}
          className={`flex-1 min-w-0 flex items-center gap-1 py-1 text-left ${
            onOpenChange ? 'cursor-pointer' : ''
          }`}
        >
          <ChevronDown
            size={14}
            className={`shrink-0 ${ACCENT_TEXT} ${MOTION} ${open ? '' : '-rotate-90'} ${
              onOpenChange ? '' : 'invisible'
            }`}
          />
          <span className={`${ROW_LABEL} !text-neutral-500`}>{label}</span>
          {summary && <span className="text-[10px] text-neutral-400 truncate ml-1">{summary}</span>}
        </button>
      </div>

      <div
        className={`${clip ? 'overflow-hidden' : ''} ${MOTION} ${
          open ? 'max-h-[400vh] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="pl-2 border-l-2 border-neutral-100 flex flex-col py-1">{children}</div>
      </div>
    </div>
  );
}
